import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatVisitApplyNumber,
  formatVisitDuration,
  formatVisitPeriod,
  getInvitationChannelLabel,
  getVisitorCompanyName,
  getVisitorContact,
  getVisitorGroupTitle,
} from "../functions/visitorHistory";
import { getVisitorHasAccessLog, getVisitorHistoryCardState, getVisitorVisitPurposeLabel } from "../functions/visitorHistoryStatus";
import type { VisitorHistoryGroup } from "../types/visitorHistory";
import { VisitorApprovalStatusBadge, VisitorHistoryCardStateLabel, VisitorVisitStatusBadge } from "./VisitorHistoryStatusBadge";

type VisitorHistoryListItemProps = {
  group: VisitorHistoryGroup;
};

const VisitorHistoryListItem = (props: VisitorHistoryListItemProps) => {
  const { t } = useTranslation(["visitor", "common"]);
  const { group } = props;
  const { representative, visitors } = group;
  const [isExpanded, setIsExpanded] = useState(false);
  const duration = formatVisitDuration(representative);
  const cardState = getVisitorHistoryCardState({
    approvalStatus: representative.approvalStatus,
    memberApprovalStatus: representative.memberApprovalStatus,
    visitStatus: representative.visitStatus,
  });

  return (
    <li className={`visitor-history-item ${isExpanded ? "expanded" : ""} ${cardState?.isDimmed ? "visitor-history-item--dimmed" : ""}`}>
      <div className="visitor-history-card__summary">
        <p className="visitor-history-card__code">{formatVisitApplyNumber(representative.visitApplyNumber)}</p>
        <p className="visitor-history-card__title">{getVisitorGroupTitle(visitors)}</p>
        <p className="visitor-history-card__period">{formatVisitPeriod(representative)}</p>
        {duration && <p className="visitor-history-card__duration">{duration}</p>}
      </div>

      <button type="button" className="visitor-history-card__toggle" aria-expanded={isExpanded} onClick={() => setIsExpanded((prev) => !prev)}>
        <span>{t("visitInformation")}</span>
        <span className="visitor-history-card__toggle-icon ic-icons-arrow-down" aria-hidden="true" />
      </button>
      <VisitorHistoryCardStateLabel cardState={cardState} />

      {isExpanded && (
        <div className="visitor-history-card__details">
          <section className="visitor-history-card__section">
            <InfoRow label={t("level1ApprovalStatus")} value={<VisitorApprovalStatusBadge approvalStatus={representative.memberApprovalStatus} />} />
            <InfoRow label={t("level2ApprovalStatus")} value={<VisitorApprovalStatusBadge approvalStatus={representative.approvalStatus} />} />
            <InfoRow label={t("visitPurpose")} value={getVisitorVisitPurposeLabel(representative.visitPurpose, representative.visitPurposeDescription)} />
            <InfoRow label={t("invitationMethod")} value={getInvitationChannelLabel(representative.invitationChannel)} />
          </section>

          <section className="visitor-history-card__visitor-section">
            <h2>{t("visitorInformation")}</h2>
            {visitors.map((visitor, index) => {
              const visitorKey = visitor.contractVisitorId || visitor.visitorMobileNumber || visitor.visitorEmail || `${group.key}-${index}`;

              return (
                <div className="visitor-history-card__visitor" key={visitorKey}>
                  <InfoRow label={t("visitorName")} value={visitor.visitorName || "-"} />
                  <InfoRow
                    label={t("visitStatus")}
                    value={
                      <VisitorVisitStatusBadge
                        visitStatus={visitor.visitStatus}
                        hasAccessLog={getVisitorHasAccessLog(visitor)}
                        isDeleted={visitor.isDeleted}
                      />
                    }
                  />
                  <InfoRow label={t("company")} value={getVisitorCompanyName(visitor)} />
                  <InfoRow label={t("contactNumber")} value={getVisitorContact(visitor)} />
                  <InfoRow label={t("vehicleNumber")} value={visitor.visitorCarNumber || "-"} />
                </div>
              );
            })}
          </section>
        </div>
      )}
    </li>
  );
};

type InfoRowProps = {
  label: string;
  value: ReactNode;
};

const InfoRow = (props: InfoRowProps) => {
  const { label, value } = props;

  return (
    <dl className="visitor-history-info-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </dl>
  );
};

export default VisitorHistoryListItem;
